<template>
	<div>
		<!-- SaleIntention ROW -->
		<div
			class="relative flex border border-solid border-gray-400 border-t-5 border-b-0 border-x-0 flex-grow"
			:class="{ 'border-t-0': first, 'bg-gray-200': open, 'rounded-br-lg': last && !open, 'rounded-bl-lg': last && !open }"
		>
			<!-- Fruchtart -->
			<div
				class="p-3 flex-grow flex flex-wrap items-center gap-x-2 bg-gray-100 w-[17.5%] min-w-16 sm:max-w-28 md:min-w-24 md:max-w-[15%]"
				:class="{ 'bg-gray-200': open, 'rounded-bl-lg': last && !open }"
			>
				{{ __("product.grades.default." + type) }}

					<span v-if="protein" class="mr-2">
						({{ __("product.proteins." + protein) }})
					</span>
			</div>

			<!-- Status -->
			<div class="hidden sm:flex p-3 text-center flex-grow bg-gray-100 py-[2px] w-[35%] min-w-24 md:min-w-24 sm:max-w-[20%]"
					 :class="{ 'bg-gray-200': open }"
			>
				<div class="relative flex w-[80%]">
					<div
						v-if="progress > 0"
						class="bg-green-200"
						:style="`width: ${progress}%`"
					></div>
					<div v-if="progress < 1" class="bg-red-400 w-1"></div>

					<div class="absolute right-2 h-full flex items-center">{{ progress }}%</div>
				</div>

				<div class="flex flex-grow justify-center items-center pl-2">
					<font-awesome-icon
						v-if="progress >= 95 && progress <= 105"
						icon="fas fa-check-square"
						class="text-xl text-green"
					/>

					<ui-tooltip
						v-else-if="progress > 105"
						class="font-normal"
						text="Achtung! Ihre verkaufte Erntemenge ist größer als Ihre geplante Erntemenge!"
					>
						<template v-slot:label>
							<ui-icon
								name="alert-triangle"
								class="w-5 h-5 text-red-600"
								stroke-width="2"
							/>
						</template>
					</ui-tooltip>
				</div>
			</div>

			<!-- Gesamt -->
			<div class="hidden sm:flex p-3 lg:pl-5 text-left flex items-center bg-gray-100 sm:bg-transparent basis-1/12 flex-grow min-w-22 sm:min-w-24 sm:max-w-[11%]"
					:class="{ 'bg-gray-200': open }"
			>
				{{ formatNumber(qty__mt, true) }} t
			</div>

			<!-- Verkauft -->
			<div class="p-3 text-right basis-1/12 flex items-center flex-grow hidden sm:flex sm:min-w-22 md:min-w-22 sm:max-w-[11%]">
        <span v-if="aggregates.qty__mt > 0">
          {{ formatNumber(aggregates.qty__mt, true) }} t
        </span>
				<span v-else>-</span>
			</div>

			<!-- Offen -->
			<div class="p-3 text-right basis-1/12 flex items-center bg-gray-100 sm:bg-transparent flex-grow sm:min-w-22 md:min-w-22 sm:max-w-[11%] font-semibold"
			:class="{ 'bg-gray-200': open }">
        <span v-if="qty__mt - aggregates.qty__mt > 0">
          {{ formatNumber((qty__mt - aggregates.qty__mt), true) }} t
        </span>
				<span v-else>-</span>
			</div>

			<!-- Ø Preis -->
			<div class="p-3 text-center basis-1/12 flex flex-grow items-center hidden sm:flex sm:min-w-24 sm:max-w-[15%]">
        <span v-if="aggregates.qty__mt > 0">
          {{ formatNumber(avgPrice) }} €/t
        </span>
				<span v-else>-</span>
			</div>

			<!-- Best Price -->
			<div class="p-3 text-center basis-1/12 flex-grow flex items-center bg-gray-100 sm:bg-transparent sm:min-w-24 md:min-w-28 sm:max-w-[15%]"
					 :class="{ 'bg-gray-200': open }"
			>
				<button
					class="cursor-pointer underline text-orange-500 underline-offset-4 font-semibold"
					:disabled="disabled"
					v-if="best_price && progress < 100 && this.acl['sale_intentions.create']"
					v-on:click="emitBidModal"
				>
					{{ formatNumber(best_price.price_estimate, false, "de-DE", 2) }} €/t
				</button>

				<button
					class="mx-2 cursor-pointer underline text-orange-500 underline-offset-4 font-semibold"
					v-else-if="best_price && !this.acl['sale_intentions.create']"
					v-on:click="
						$bus.$emit('modal:open', {
							view: {
								view: 'views/Modal/PremiumRequired',
							}
						})
					"
				>
					xxx €/t
				</button>
				<span v-else>-</span>
			</div>

			<!-- Aktionen -->
			<div class="p-3 flex items-center justify-end bg-gray-100 sm:bg-transparent md:min-w-24 rounded-br-lg"
					 :class="{ 'bg-gray-200': open }"
			>
				<!-- 👉 Toggle NUR wenn mehrere locations -->

				<button
					v-on:click="toggle"
					class="font-normal hover:bg-gray-200 rounded-lg p-2 inline text-orange-500"
					:class="{ 'md:hidden': !hasMultipleLocations }"
				>
					<font-awesome-icon
						v-if="open"
						icon="fa-sold fa-chevron-up"
						class="text-xl"
					/>

					<font-awesome-icon
						v-else
						icon="fa-sold fa-chevron-down"
						class="text-xl"
					/>

				</button>

				<!-- Edit nur wenn EIN Datensatz -->
				<ui-tooltip v-if="!hasMultipleLocations" class="font-normal" text="Fruchtart bearbeiten">
					<template v-slot:label>
						<button
							:disabled="disabled"
							v-on:click="emitEditModal"
							class="text-orange-500 hover:bg-gray-200 rounded-lg p-2 hidden md:inline-block"
						>
							<ui-icon class="h-5 w-5" name="edit" stroke-width="2.5" />
						</button>
					</template>
				</ui-tooltip>

				<!-- Delete nur wenn EIN Datensatz -->
				<ui-tooltip v-if="!hasMultipleLocations" class="font-normal" text="Fruchtart löschen">
					<template v-slot:label>
						<button
							v-on:click="emitDeleteModal"
							class="text-orange-500 hover:bg-gray-200 rounded-lg p-2 hidden md:inline-block"
						>
							<ui-icon class="h-5 w-5" name="trash" stroke-width="2.5" />
						</button>
					</template>
				</ui-tooltip>


			</div>
		</div>

		<!-- 👇 locations -->
		<UserLocation
			v-if="locations.length && open"
			v-for="l in locations"
			:key="l.sale_intention_id"
			v-bind="l"
		/>
	</div>
</template>

<script>
import UserLocation from "./UserLocation.vue";

export default {
	components: { UserLocation },

	props: {
		disabled: Boolean,

		id: Number,
		type: String,
		protein: String,
		qty__mt: Number,
		aggregates: Object,
		progress: Number,
		best_price: Object,

		locations: {
			type: Array,
			default: () => [],
		},

		first: Boolean,
		last: Boolean,
	},
	data() {
		return {
			open: false,
			acl: this.$inertia.page.props.acl,
		};
	},
	computed: {
		hasMultipleLocations() {
			return this.locations && this.locations.length > 1;
		},

		avgPrice() {
			if (!this.aggregates?.qty__mt) return 0;
			return this.aggregates.price__mt / this.aggregates.qty__mt;
		},
	},

	methods: {
		toggle() {
			this.open = !this.open;
		},
		emitEditModal() {
			this.$bus.$emit('modal:open', {
				view: {
					view: 'views/SaleIntention/Form',
					dataUrl: this.$route('sale_intentions.update', {
						id: this.locations[0]?.sale_intention_id,
					}),
				},
				cta: {
					code: 402,
					view: 'views/Modal/PremiumRequired'
				}
			})
		},
		emitDeleteModal() {
			this.$bus.$emit('modal:open', {
				view: {
					view: 'views/SaleIntention/Delete',
					data: { id: this.locations[0]?.sale_intention_id }
				},
				size: 'sm'
			})
		},
		emitBidModal() {
			this.$bus.$emit(
				'toggle-iframe-modal',
				this.$route('bid.create', {
					targetType: 'BUY',
					targetId: this.best_price.id,
					sourcePriceIndicationId: this.best_price.price_indication_id,
					lastDisplayedPrice: this.formatNumber(this.best_price.price_estimate, false, 'en-US', 2),
				}) + '&iframe_modal=true'
			)
		},
		formatNumber(number, grouping = false, lang = "de-DE", digits = 0) {
			return new Intl.NumberFormat(lang, {
				useGrouping: grouping,
				minimumFractionDigits: digits,
				maximumFractionDigits: digits,
			}).format(number);
		},
	},
};
</script>
